// Multi-territory Compliance Master screen.
// Ported from the India ComplianceMaster (src/components/admin/ComplianceMaster.jsx)
// using the MT compliance-master / entity / TAN APIs.

import { saveBlobAsFile } from '@/api/apiUtils'
import DeleteIcon from '@/assets/icons/DeleteIcon'
import DownloadIcon from '@/assets/icons/DownloadIcon'
import SearchIconBtn from '@/assets/icons/SearchIconBtn'
import { ComplianceDeleteDialog } from '@/components/admin'
import { CommonModal, CommonTable, Search } from '@/components/common'
import { GLOBAL_CONSTANTS } from '@/constants/globalConstants'
import { DEFAULT_PAGE, getValidCriteria } from '@/constants/index'
import { useSnackbar } from '@/contexts/SnackbarProvider'
import {
    MT_COMPLIANCE_MASTER_COLUMNS,
    MT_COMPLIANCE_MASTER_SEARCH_FIELDS,
} from '@/multi-territory/mt-configs/mt-compliance-master'
import { COUNTRY_OPTIONS } from '@/multi-territory/mt-constants/mt-country-default'
import {
    getEntityDropdownByCountry,
    getTanDropdownByEntities,
} from '@/multi-territory/mt-services/mt-assign-entity-api'
import {
    createComplianceEntry,
    deleteComplianceEntries,
    downloadComplianceExcel,
    getComplianceCategoryList,
    getComplianceEntryList,
    updateComplianceEntry,
    type MTComplianceCreateRequest,
} from '@/multi-territory/mt-services/mt-compliance-master-api'
import { MTCountryOption } from '@/multi-territory/mt-services/mt-entity-api'
import { getFinancialYearForDate } from '@/utils/complianceMasterUtils'
import { ROLE_CAPABILITIES } from '@/utils/role-capabilities'
import { Box, Button, IconButton, Tooltip, Typography } from '@mui/material'
import dayjs from 'dayjs'
import { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import {
    MTAdminComplianceMasterForm,
    MTComplianceCountryOption,
    type MTComplianceCategoryOption,
    type MTComplianceEntityOption,
    type MTComplianceFormValues,
    type MTComplianceTanItem,
} from './MTAdminComplianceMasterForm'

const SearchComp = Search as any
const CommonModalComp = CommonModal as any
const CommonTableComp = CommonTable as any

// The categories API may return an array of strings; the form expects objects
// with a `name`, so normalize strings into { name } shape.
const normalizeCategories = (
  data: unknown = []
): MTComplianceCategoryOption[] =>
  (Array.isArray(data) ? data : []).map((item: any) =>
    typeof item === 'string' ? { name: item } : item
  )

// Convert a 'DD/MM/YYYY' string to the API's 'YYYY-MM-DD' start date.
const toApiDate = (ddmmyyyy: string): string => {
  if (!ddmmyyyy) return ''
  const [day, month, year] = ddmmyyyy.split('/')
  if (!day || !month || !year) return ''
  return `${year}-${month}-${day}`
}

// Convert an ISO API date ("2026-01-31") to 'DD/MM/YYYY'.
const fromApiDate = (value: unknown): string => {
  if (!value) return ''
  const parsed = dayjs(String(value))
  if (!parsed.isValid()) return String(value)
  return parsed.format('DD/MM/YYYY')
}

// Normalize an API list row: alias uuid for row selection and format the date
// fields for display.
const mapComplianceRow = (entry: any) => ({
  ...entry,
  uuid: entry.uuid || entry.id,
  complianceDueDate: fromApiDate(entry.complianceDueDate),
})

const DEFAULT_FORM_VALUES: MTComplianceFormValues = {
  country: '',
  entityNames: [],
  entityTANs: [],
  complianceCategories: [],
  compliancePeriod: '',
  compliancePeriodDate: '',
  dueDate: '',
  status: '',
  completionDate: '',
}

interface MTAdminComplianceMasterProps {
  roleConfig?: { canEdit: boolean }
}

export function MTAdminComplianceMaster({
  roleConfig: roleConfigProp,
}: MTAdminComplianceMasterProps) {
  // Derive capabilities from the logged-in user's role (e.g. PREPARER cannot
  // add/delete compliances). A caller-supplied roleConfig still takes priority.
  const roleTypes = useSelector((state: any) => state.user.role)
  const roleConfig =
    roleConfigProp ||
    ROLE_CAPABILITIES[roleTypes as keyof typeof ROLE_CAPABILITIES] ||
    ROLE_CAPABILITIES.default
  const [entries, setEntries] = useState<any[]>([])
  const [loading, setLoading] = useState(true)

  const [categories, setCategories] = useState<MTComplianceCategoryOption[]>([])

  // Entity/TAN options for the selected country (India included), fetched on
  // demand via the Assign Entity dropdown APIs when a country / entities are chosen.
  const [countryEntities, setCountryEntities] = useState<
    MTComplianceEntityOption[]
  >([])
  const [countryTanList, setCountryTanList] = useState<MTComplianceTanItem[]>(
    []
  )

  // Country options for the Add modal (from redux navigation, else defaults).
  const reduxCountryOptions: MTCountryOption[] =
    useSelector((state: any) => state.navigation.countryOptions) ||
    COUNTRY_OPTIONS
  const sourceCountryList: MTCountryOption[] =
    reduxCountryOptions && reduxCountryOptions.length
      ? reduxCountryOptions
      : COUNTRY_OPTIONS
  const countryOptions: MTComplianceCountryOption[] = sourceCountryList.map(
    c => ({ countryCode: c.country_code, countryName: c.country_name })
  )

  // Resolve a country code to its UUID (needed by the Assign-Entity dropdown API).
  const getCountryUuid = (countryCode: string): string =>
    sourceCountryList.find(c => c.country_code === countryCode)?.uuid || ''

  // Is the selected country India? Only used to normalize the India country
  // code/name for the compliance-master APIs. Match by code OR name (case-insensitive) because the country
  // list may come from redux where India's code could be 'IND', 'INDIA', etc.
  const isIndia = (countryCode: string): boolean => {
    if (!countryCode) return false
    const code = String(countryCode).trim().toUpperCase()
    if (
      code === String(GLOBAL_CONSTANTS.INDIA_COUNTRY_CODE).toUpperCase() ||
      code === 'INDIA'
    ) {
      return true
    }
    // Fall back to resolving the selected code to its country name.
    const match = sourceCountryList.find(
      c => String(c.country_code).toUpperCase() === code
    )
    return (
      String(match?.country_name || '')
        .trim()
        .toUpperCase() === 'INDIA'
    )
  }

  // Country code sent to the compliance-master APIs (categories + create).
  // India's code from redux may vary ('IND', 'INDIA', ...) — always send the
  // canonical India code so the shared India backend resolves it.
  const toApiCountryCode = (countryCode: string): string =>
    isIndia(countryCode)
      ? GLOBAL_CONSTANTS.INDIA_COUNTRY_CODE_COMPLIANCE
      : countryCode

  const [modalOpen, setModalOpen] = useState(false)
  const [formMode, setFormMode] = useState<'add' | 'edit'>('add')
  const [editingEntryId, setEditingEntryId] = useState<string | null>(null)
  const [formValues, setFormValues] =
    useState<MTComplianceFormValues>(DEFAULT_FORM_VALUES)
  const [formErrors, setFormErrors] = useState<Record<string, string>>({})
  const [formLoading, setFormLoading] = useState(false)

  // Entity + TAN options for every country come from the Assign-Entity
  // dropdown APIs; nothing is shown until a country is chosen.
  const entities: MTComplianceEntityOption[] = formValues.country
    ? countryEntities
    : []
  const tanList: MTComplianceTanItem[] = formValues.country
    ? countryTanList
    : []

  const [selectedItems, setSelectedItems] = useState<string[]>([])
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deleteTargets, setDeleteTargets] = useState<string[]>([])

  // Search state
  const [searchOpen, setSearchOpen] = useState(false)
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [searchCriteria, setSearchCriteria] = useState([
    {
      id: 1,
      logicalOperator: 'Where',
      field: MT_COMPLIANCE_MASTER_SEARCH_FIELDS[0]?.id || '',
      value: '',
    },
  ])

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState<number>(
    GLOBAL_CONSTANTS.DEFAULT_PAGE_SIZE
  )
  const [totalItems, setTotalItems] = useState(0)

  const { showSnackbar } = useSnackbar()

  const hasLoadedData = useRef(false)

  const loadAllData = async (
    page = 1,
    pageSize = itemsPerPage,
    searchList: unknown[] = []
  ): Promise<void> => {
    setLoading(true)
    try {
      // On mount we only load the table list. Compliance categories are
      // country-scoped and fetched when the user selects a country in the Add
      // modal; Entity Name / Entity TAN options are likewise country-driven
      // (Assign-Entity dropdown APIs for every country).
      const entriesResponse = await getComplianceEntryList<any>({
        pageFilter: {
          currentPage: page,
          includeCount: true,
          pageSize,
          sortField: 'createdDate',
          sortOrder: 'DESC',
        },
        requestFilter: [],
        isReportDownload: false,
        searchCOList: searchList,
      })

      if (entriesResponse.success) {
        const data: any = entriesResponse.data
        const rawContent =
          data?.data?.content || data?.content || data?.data || data || []
        const content = (Array.isArray(rawContent) ? rawContent : []).map(
          mapComplianceRow
        )
        setEntries(content)
        setTotalItems(
          data?.data?.totalElements ||
            data?.totalElements ||
            content.length ||
            0
        )
        setCurrentPage(page)
      } else {
        showSnackbar(entriesResponse.error, 'error')
      }
    } catch {
      console.error('Error loading MT Compliance Master data')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (hasLoadedData.current) return
    hasLoadedData.current = true
    loadAllData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {}
    if (!formValues.country) {
      errors.country = 'Country is required'
    }
    if (!formValues.entityNames || formValues.entityNames.length === 0) {
      errors.entityNames = 'At least one Entity Name is required'
    }
    if (!formValues.entityTANs || formValues.entityTANs.length === 0) {
      errors.entityTANs = 'At least one Entity TAN is required'
    }
    if (
      !formValues.complianceCategories ||
      formValues.complianceCategories.length === 0
    ) {
      errors.complianceCategories =
        'At least one Compliance Category is required'
    }
    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  // Fetch entity options for the selected country (India included) via the
  // Assign-Entity dropdown API (by country UUID).
  const fetchCountryEntities = async (countryCode: string): Promise<void> => {
    const countryUuid = getCountryUuid(countryCode)
    if (!countryUuid) {
      setCountryEntities([])
      return
    }
    const response = await getEntityDropdownByCountry([countryUuid])
    if (response.success && Array.isArray(response.data)) {
      setCountryEntities(
        response.data.map(item => ({
          id: item.uuid,
          uuid: item.uuid,
          name: item.entityName,
          countryCode,
        }))
      )
    } else {
      setCountryEntities([])
    }
  }

  // Fetch TAN (TIN/UEN) options via the Assign-Entity dropdown API (by
  // selected entity UUIDs).
  const fetchCountryTans = async (entityNames: string[]): Promise<void> => {
    const entityUuids = entityNames
      .map(name => countryEntities.find(e => e.name === name)?.uuid)
      .filter(Boolean) as string[]
    if (entityUuids.length === 0) {
      setCountryTanList([])
      return
    }
    const response = await getTanDropdownByEntities(entityUuids)
    if (response.success && Array.isArray(response.data)) {
      const mappedTans = response.data.map(item => {
        const attrs = (item.attributes || {}) as Record<string, unknown>
        // The TIN/UEN value key varies per country (e.g. Singapore uses UEN),
        // so try the common attribute keys before falling back to the uuid.
        const tinValue =
          (attrs.tax_identification_number as string) ||
          (attrs.tax_deduction_account_number as string) ||
          (attrs.uin as string) ||
          (attrs.uen as string) ||
          (attrs.tin as string) ||
          item.uuid
        // Resolve the entity name from the selected country entities by UUID
        // so it EXACTLY matches the Entity Name option the form filters on
        // (the TAN dropdown is populated by matching tan.entityName === name).
        const resolvedEntityName =
          countryEntities.find(e => e.uuid === item.entityMasterUuid)?.name ||
          item.entityName
        return {
          ...item,
          tan: tinValue,
          entityName: resolvedEntityName,
          uuid: item.uuid,
          entityUuid: item.entityMasterUuid,
        }
      })
      setCountryTanList(mappedTans)
      // Auto-select the fetched TIN/UEN values for the chosen entities.
      const autoSelectedTans = [...new Set(mappedTans.map(t => t.tan))]
      setFormValues(prev => ({ ...prev, entityTANs: autoSelectedTans }))
    } else {
      setCountryTanList([])
    }
  }

  const handleAddClick = async (): Promise<void> => {
    const today = dayjs()
    setFormMode('add')
    setEditingEntryId(null)
    // Country starts empty — the user must explicitly select a country before
    // Entity Name / Entity TAN options are fetched.
    setFormValues({
      ...DEFAULT_FORM_VALUES,
      country: '',
      compliancePeriod: getFinancialYearForDate(today.toDate()),
      compliancePeriodDate: today.format('DD/MM/YYYY'),
    })
    setFormErrors({})
    // Country starts empty, so clear ALL entity/TAN options. They are fetched
    // on country select via the Assign-Entity API.
    setCountryEntities([])
    setCountryTanList([])
    // Categories are country-scoped — clear them until a country is picked;
    // they are (re)fetched for the chosen country in handleFieldChange.
    setCategories([])
    setModalOpen(true)
  }

  // Fetch the compliance categories valid for the given country
  // (.../categories?countryCode=XX). Returns true when at least one category
  // is available — the caller uses this to decide whether to load the
  // Entity / TAN options (no categories ⇒ nothing can be created for this
  // country, so the entity/TAN APIs are skipped).
  const fetchCategoriesForCountry = async (
    countryCode: string
  ): Promise<boolean> => {
    if (!countryCode) {
      setCategories([])
      return false
    }
    const categoriesResponse = await getComplianceCategoryList<any>(
      toApiCountryCode(countryCode)
    )
    if (categoriesResponse.success) {
      const normalized = normalizeCategories(categoriesResponse.data)
      setCategories(normalized)
      return normalized.length > 0
    }
    setCategories([])
    showSnackbar(categoriesResponse.error, 'error')
    return false
  }

  const handleEditClick = (row: any): void => {
    setFormMode('edit')
    setEditingEntryId(row.uuid || row.id)
    setFormValues({
      country: row.countryCode || '',
      entityNames: row.entityName ? [row.entityName] : [],
      entityTANs: row.tanName ? [row.tanName] : [],
      complianceCategories: row.complianceCategory
        ? [row.complianceCategory]
        : [],
      compliancePeriod: row.compliancePeriod || '',
      compliancePeriodDate: fromApiDate(row.complianceStartDate),
      dueDate: row.complianceDueDate || '',
      status: row.complianceStatus || '',
      completionDate: row.complianceCompletionDate || '',
    })
    setFormErrors({})
    setModalOpen(true)
  }

  const handleModalClose = (): void => {
    setModalOpen(false)
    setFormErrors({})
  }

  const handleFieldChange = (
    field: keyof MTComplianceFormValues,
    value: unknown
  ): void => {
    // Changing country invalidates entity + TAN selections (country-scoped).
    if (field === 'country') {
      const newCountry = value as string
      setFormValues(prev => ({
        ...prev,
        country: newCountry,
        entityNames: [],
        entityTANs: [],
        // Categories are country-scoped — clear the previous selection.
        complianceCategories: [],
      }))
      // Reset all entity/TAN options, then fetch for the chosen country via
      // the Assign-Entity dropdown API (by country UUID) — India included.
      setCountryEntities([])
      setCountryTanList([])
      // Categories depend on the country — refetch for the chosen country.
      // Only load the Entity / TAN options when the country actually has
      // compliance categories; if categories come back null/empty there is
      // nothing to create, so we skip the entity + TAN APIs.
      if (newCountry) {
        void fetchCategoriesForCountry(newCountry).then(hasCategories => {
          if (!hasCategories) return
          fetchCountryEntities(newCountry)
        })
      } else {
        setCategories([])
      }
      if (formErrors[field]) {
        setFormErrors(prev => ({ ...prev, [field]: '' }))
      }
      return
    }

    if (field === 'entityNames' || field === 'entityTANs') {
      // Update the selection as-is; when entities change, fetch the TAN
      // (TIN/UEN) options for the chosen entities (auto-selected on load).
      setFormValues(prev => {
        const next = { ...prev, [field]: value as string[] }
        if (field === 'entityNames') {
          next.entityTANs = []
        }
        return next
      })
      if (field === 'entityNames') {
        fetchCountryTans(value as string[])
      }
      if (formErrors[field]) {
        setFormErrors(prev => ({ ...prev, [field]: '' }))
      }
      return
    }

    setFormValues(prev => ({ ...prev, [field]: value }))
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }))
    }
  }

  const handleFormSubmit = async (
    e: React.FormEvent | React.MouseEvent
  ): Promise<void> => {
    e.preventDefault()

    if (formMode === 'edit') {
      setFormLoading(true)
      try {
        const response = await updateComplianceEntry(editingEntryId as string, {
          completionDate: toApiDate(formValues.completionDate),
        })
        if (response.success) {
          setModalOpen(false)
          setEditingEntryId(null)
          showSnackbar('Compliance updated successfully', 'success')
          await loadAllData(
            currentPage,
            itemsPerPage,
            getValidCriteria(searchCriteria)
          )
        } else {
          showSnackbar(response.error, 'error')
        }
      } catch (error: any) {
        showSnackbar(
          error?.message || 'Failed to update Compliance entry',
          'error'
        )
      } finally {
        setFormLoading(false)
      }
      return
    }

    if (!validateForm()) return

    setFormLoading(true)
    try {
      const entityUuids = [
        ...new Set(
          formValues.entityNames
            .map(name => entities.find(en => en.name === name)?.uuid)
            .filter(Boolean) as string[]
        ),
      ]
      const tanUuids = [
        ...new Set(
          formValues.entityTANs
            .map(tan => (tanList.find(item => item.tan === tan) as any)?.uuid)
            .filter(Boolean) as string[]
        ),
      ]

      // Resolve the selected country so we can send the backend-required
      // `countryName` and decide the identifier scoping. `tax_registration_required`
      // is the country-level equivalent of the MT Key Filter `tanApplicable`
      // flag:
      //  - TAN applicable  → send BOTH `tanUuids` and `entityUuids`.
      //  - TAN not applicable → send only `entityUuids`.
      const selectedCountry = sourceCountryList.find(
        c => c.country_code === formValues.country
      )
      const countryName = selectedCountry?.country_name || ''
      const tanEnabled = !!selectedCountry?.tax_registration_required

      const payload: MTComplianceCreateRequest = {
        countryCode: toApiCountryCode(formValues.country),
        countryName: isIndia(formValues.country)
          ? GLOBAL_CONSTANTS.INDIA_COUNTRY
          : countryName,
        complianceCategories: formValues.complianceCategories,
        startDate: toApiDate(formValues.compliancePeriodDate),
        entityUuids,
        ...(tanEnabled ? { tanUuids } : {}),
      }

      const response = await createComplianceEntry<any>(payload)
      if (response.success) {
        setModalOpen(false)
        setFormValues({ ...DEFAULT_FORM_VALUES })
        const data: any = response.data
        if (data?.success) {
          if (data?.meta?.message) {
            showSnackbar(data.meta.message, 'success')
          } else {
            showSnackbar('Compliance created successfully', 'success')
          }
        } else if (data?.errors?.length) {
          showSnackbar(data.errors[0]?.message, 'error')
        } else {
          showSnackbar('Compliance created successfully', 'success')
        }
        await loadAllData(1, itemsPerPage, getValidCriteria(searchCriteria))
      } else {
        setFormErrors({
          general: response.error || 'Failed to create Compliance',
        })
      }
    } catch (error: any) {
      setFormErrors({
        general: error?.message || 'An unexpected error occurred',
      })
    } finally {
      setFormLoading(false)
    }
  }

  // Selection handlers
  const handleSelectItem = (itemId: string): void => {
    setSelectedItems(prev =>
      prev.includes(itemId)
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    )
  }

  const handleSelectAll = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    if (event.target.checked) {
      setSelectedItems(entries.map(item => item.uuid || item.id))
    } else {
      setSelectedItems([])
    }
  }

  // Delete handlers
  const handleBulkDeleteClick = (): void => {
    setDeleteTargets([...selectedItems])
    setDeleteDialogOpen(true)
  }

  const handleCancelDelete = (): void => {
    setDeleteDialogOpen(false)
    setDeleteTargets([])
  }

  const handleConfirmDelete = async (): Promise<void> => {
    try {
      const response = await deleteComplianceEntries(deleteTargets)
      if (response.success) {
        const targetSet = new Set(deleteTargets)
        setEntries(prev =>
          prev.filter(entry => !targetSet.has(entry.uuid || entry.id))
        )
        setTotalItems(prev => Math.max(0, prev - deleteTargets.length))
        setSelectedItems(prev => prev.filter(id => !targetSet.has(id)))
        setDeleteDialogOpen(false)
        setDeleteTargets([])
        showSnackbar(
          'Selected Compliance(s) are successfully deleted',
          'success'
        )
      } else {
        showSnackbar(response.error, 'error')
      }
    } catch (error: any) {
      showSnackbar(
        error?.message || 'Failed to delete Compliance entries',
        'error'
      )
    }
  }

  // Pagination handlers
  const handlePageChange = (page: number): void => {
    loadAllData(page, itemsPerPage, getValidCriteria(searchCriteria))
  }

  const handleItemsPerPageChange = (newItemsPerPage: number): void => {
    setItemsPerPage(newItemsPerPage)
    setCurrentPage(1)
    loadAllData(1, newItemsPerPage, getValidCriteria(searchCriteria))
  }

  // Download handler
  const handleDownload = async (): Promise<void> => {
    if (totalItems === 0) {
      showSnackbar('No Data Found', 'info')
      return
    }
    try {
      const payload = {
        pageFilter: {
          currentPage,
          includeCount: true,
          pageSize: DEFAULT_PAGE,
          sortField: 'createdDate',
          sortOrder: 'DESC' as const,
        },
        requestFilter: [],
        isReportDownload: true,
        searchCOList: getValidCriteria(searchCriteria),
      }
      const response = await downloadComplianceExcel(payload)
      if (response.success && response.data) {
        saveBlobAsFile(response.data, 'assign_compliances.xlsx')
      } else {
        showSnackbar(
          (response as any).error || 'Failed to download excel',
          'error'
        )
      }
    } catch (e: any) {
      showSnackbar(e?.message || 'Failed to download excel', 'error')
    }
  }

  // Search handlers
  const handleSearch = (event: React.MouseEvent<HTMLElement>): void => {
    setSearchOpen(!searchOpen)
    setAnchorEl(event.currentTarget)
  }

  const handleApplySearch = async (searchList: any[]): Promise<void> => {
    const mappedCriteria = searchList.map((item, idx) => ({
      ...item,
      field: item.fieldName || item.field || '',
      id: idx + 1,
      logicalOperator: item.logicalOperator || (idx === 0 ? 'Where' : 'And'),
    }))
    setSearchCriteria(mappedCriteria)
    setCurrentPage(1)
    await loadAllData(1, itemsPerPage, searchList)
    setSearchOpen(false)
  }

  const handleResetSearch = async (): Promise<void> => {
    setSearchCriteria([
      {
        id: 1,
        logicalOperator: 'Where',
        field: MT_COMPLIANCE_MASTER_SEARCH_FIELDS[0]?.id || '',
        value: '',
      },
    ])
    setCurrentPage(1)
    await loadAllData(1, itemsPerPage, [])
    setSearchOpen(false)
  }

  const handleCloseSearch = (): void => {
    setSearchOpen(false)
    setAnchorEl(null)
  }

  return (
    <Box>
      {/* Sub Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          minHeight: '32px',
          py: '12px',
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontSize: '20px',
            color: 'var(--forbrand-secondary)',
            fontWeight: 500,
          }}
        >
          {GLOBAL_CONSTANTS.MT_ADMIN_COMPLIANCE_MASTER_TITLE}
        </Typography>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            position: 'relative',
          }}
        >
          <Tooltip title={GLOBAL_CONSTANTS.DOWNLOAD_EXCEL}>
            <IconButton onClick={handleDownload}>
              <DownloadIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title={GLOBAL_CONSTANTS.SEARCH}>
            <IconButton onClick={handleSearch}>
              <SearchIconBtn />
            </IconButton>
          </Tooltip>

          {selectedItems.length > 0 && roleConfig.canEdit && (
            <>
              <Typography
                sx={{
                  height: '16px',
                  borderLeft: '1px solid var(--forbrand-color)',
                  marginRight: '6px',
                }}
              ></Typography>
              <Tooltip title="Delete">
                <IconButton onClick={handleBulkDeleteClick}>
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            </>
          )}

          {roleConfig.canEdit && (
            <>
              <Typography
                sx={{
                  height: '16px',
                  borderLeft: '1px solid var(--forbrand-color)',
                  marginRight: '6px',
                }}
              ></Typography>

              <Button
                variant="contained"
                onClick={handleAddClick}
                sx={{
                  backgroundColor: 'var( --forbrand-bg-variant)',
                  color: 'var(--forbrand-brand-varient)',
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '16px',
                }}
              >
                Add
              </Button>
            </>
          )}
        </Box>
      </Box>

      {/* Table */}
      <Box>
        <CommonTableComp
          columns={MT_COMPLIANCE_MASTER_COLUMNS}
          data={entries}
          loading={loading}
          emptyMessage="No compliance entries found"
          showFooter={true}
          totalItems={totalItems}
          currentPage={currentPage}
          itemsPerPage={itemsPerPage}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          showItemsPerPage={true}
          showTotalItems={true}
          showPageNumbers={true}
          showActions={false}
          showCheckbox={roleConfig.canEdit}
          selectedItems={selectedItems}
          onSelectItem={handleSelectItem}
          onSelectAll={handleSelectAll}
        />
      </Box>

      {/* Add/Edit Compliance Modal */}
      <CommonModalComp
        open={modalOpen}
        onClose={handleModalClose}
        title={formMode === 'edit' ? 'Edit Compliance' : 'Add Compliance'}
        maxWidth="sm"
      >
        <MTAdminComplianceMasterForm
          mode={formMode}
          values={formValues}
          countryOptions={countryOptions}
          entities={entities}
          tanList={tanList}
          categories={categories}
          formErrors={formErrors}
          formLoading={formLoading}
          onFieldChange={handleFieldChange}
          onSubmit={handleFormSubmit}
        />
      </CommonModalComp>

      {/* Delete Confirmation Dialog */}
      <ComplianceDeleteDialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        onCancel={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        count={deleteTargets.length}
      />

      <SearchComp
        open={searchOpen}
        onClose={handleCloseSearch}
        onApply={handleApplySearch}
        onReset={handleResetSearch}
        anchorEl={anchorEl}
        fields={MT_COMPLIANCE_MASTER_SEARCH_FIELDS}
        placement="bottom-start"
        setSearchCriteria={setSearchCriteria}
        searchCriteria={searchCriteria}
      />
    </Box>
  )
}

export default MTAdminComplianceMaster
